import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import NativeSelect from "@mui/material/NativeSelect";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type { MessagePurgeRequest } from "@bw/shared/types/generated";
import { purgeMessageContent } from "../api";

type PurgeBasis = MessagePurgeRequest["basis"];
type PurgeCommand = {
  messageId: string;
  request: MessagePurgeRequest;
};

export function MessagePurgeAction() {
  const [messageId, setMessageId] = useState("");
  const [basis, setBasis] = useState<PurgeBasis>("legal_request");
  const [caseReference, setCaseReference] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const purge = useMutation({
    mutationFn: (command: PurgeCommand) =>
      purgeMessageContent(command.messageId, command.request),
  });

  const canSubmit =
    messageId.trim().length > 0 &&
    caseReference.trim().length > 0 &&
    confirmation === "PURGE" &&
    !purge.isPending;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;
    purge.mutate({
      messageId: messageId.trim(),
      request: {
        basis,
        case_reference: caseReference.trim(),
        confirmation: "PURGE",
      },
    });
  }

  function resetSettledMutation() {
    if (!purge.isPending) purge.reset();
  }

  return (
    <Card variant="outlined" component="section">
      <CardContent>
        <Stack component="form" spacing={2} onSubmit={submit}>
          <div>
            <Typography component="h2" variant="h6">
              Purge direct message content
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Use an exact message ID from an approved legal or safety case.
              This action permanently removes its content and cannot be undone.
            </Typography>
          </div>

          <TextField
            label="Message ID"
            required
            size="small"
            disabled={purge.isPending}
            value={messageId}
            onChange={(event) => {
              setMessageId(event.target.value);
              resetSettledMutation();
            }}
            slotProps={{ htmlInput: { autoComplete: "off" } }}
          />

          <FormControl size="small">
            <InputLabel htmlFor="message-purge-basis">Basis</InputLabel>
            <NativeSelect
              id="message-purge-basis"
              disabled={purge.isPending}
              value={basis}
              onChange={(event) => {
                setBasis(event.target.value as PurgeBasis);
                resetSettledMutation();
              }}
            >
              <option value="legal_request">Legal request</option>
              <option value="platform_safety">Platform safety</option>
            </NativeSelect>
          </FormControl>

          <TextField
            label="Case reference"
            required
            size="small"
            disabled={purge.isPending}
            value={caseReference}
            onChange={(event) => {
              setCaseReference(event.target.value);
              resetSettledMutation();
            }}
            helperText="Enter the approved case or request reference."
            slotProps={{
              htmlInput: { autoComplete: "off", maxLength: 100 },
            }}
          />

          <TextField
            label="Type PURGE to confirm"
            required
            size="small"
            disabled={purge.isPending}
            value={confirmation}
            onChange={(event) => {
              setConfirmation(event.target.value);
              resetSettledMutation();
            }}
            slotProps={{ htmlInput: { autoComplete: "off" } }}
          />

          {purge.isError && (
            <Alert severity="error">
              Message content could not be purged. Verify the approved case and
              try again.
            </Alert>
          )}
          {purge.data && (
            <Alert severity="success">
              {purge.data.status === "already_purged"
                ? "Message content was already purged."
                : "Message content was permanently purged."}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            color="error"
            size="small"
            disabled={!canSubmit}
            sx={{ alignSelf: "flex-start" }}
          >
            {purge.isPending ? "Purging…" : "Purge message content"}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
